
import mixpanel from 'mixpanel-browser';
import moment from 'moment';
import { classNames } from 'primereact/utils';
import React, { useEffect, useState } from 'react';
import { Chart } from 'react-google-charts';
import flyerPdf from '../../assets/files/NowFlyerEng.pdf';
import QRfigure from '../../assets/qr-figure.png';
import { BrandingIconCard, Button, CurrencyAmount, Loader, NamedLink } from '../../components';
import { mixPanelEventsConstants, mixPanelEventsData, mixPanelEventsScreens } from '../../mixpanel';
import { FormattedMessage } from '../../util/reactIntl';
import {
  DEAL_LISTING_TYPE,
  PRODUCT_LISTING_TYPE,
  SERVICE_LISTING_TYPE,
  STAFF_LISTING_TYPE
} from '../../util/types';
import css from './DashboardData.module.css';
import { trackEventsMixpanel } from '../../mixpanel/mixpanel';

const BrandingIconTypes = {
  DEALS: 'deals',
  SOLD: 'sold',
  TRENDUP: 'trentup',
  TOTAL: 'total',
  ACTIVE_ITEMS: 'active-items',
  ACTIVE_DEALS: 'active-deals',
  HEALTH: 'health',
};

const badgeIcon = {
  BUCKET: 'bucket',
  BUZZ: 'buzz',
  SCANQR: 'scanqr',
  ACCEPTCODE: 'acceptcode',
  PEOPLE: 'people',
  WALLET: 'wallet',
  SHARE_LINK: "share-link"
};

const allProcessNames = {
  [DEAL_LISTING_TYPE]: ["default-deal-booking", "default-deal-purchase"],
  [SERVICE_LISTING_TYPE]: ["default-booking"],
  [PRODUCT_LISTING_TYPE]: ["default-purchase"],
  [STAFF_LISTING_TYPE]: ["default-booking"],
  ["none"]: ["default-booking", "default-deal-booking", "default-deal-purchase"],
}


const DashboardData = (props) => {
  const {
    activeDeals = [],
    totalDeals,
    totalSales,
    soldToday,
    listingType = 'none',
    intl,
    fetchAnalyticsRequest,
    analyticsData,
    fetchAnalyticsError,
    currentUser,
    onReFetchAnalytics,
    downloadReportRequest,
    downloadReportSuccess,
    downloadReportError,
    onHandleDownloadReport
  } = props;

  const isDeal = listingType === DEAL_LISTING_TYPE;
  const isHomePage = listingType === "none";
  const currentUserId = currentUser?.id.uuid;

  const {
    top_4_services,
    sales,
    revenue,
    today_sales,
    completed_items,
    purchased_items,
    filtered_sales,
    filtered_revenue,
    filtered_today_sales,
    filtered_completed_items,
    filtered_purchased_items,
    staff_details,
    weekly_payments,
  } = analyticsData || {};

  const pieChartData = [
    ['Status', 'Count'],
    ['Done', isHomePage ? completed_items : filtered_completed_items],
    ['Paid', isHomePage ? purchased_items : filtered_purchased_items],
  ];

  // Check if pieChartData has valid data
  const pieChartHasData = pieChartData[1][1] > 0 || pieChartData[2][1] > 0;

  const pieChartOptions = {
    sliceVisibilityThreshold: 0.02, // Hides slices smaller than 2%
    legend: {
      position: "bottom",
      alignment: "center",
      textStyle: {
        color: "#233238",
        fontSize: 12,
      },
    },
    colors: ["#FFC416", "#70B6C1", "#FFB1B7", "#BCD18A", "#D1C28A"],
  };

  const transformedServiceData = Array.isArray(top_4_services) && top_4_services?.length > 0
    ? [
      ["Item", "Revenue"], // Headers
      ...top_4_services.map(service => [
        service?.title,
        (() => {
          const n = Number(service?.revenue);
          return Number.isFinite(n) ? n : 0;
        })(),
      ])
    ]
    : [["Item", "Revenue"]];  // If no data, just show headers

  const top4ServicesOptions = {
    pieHole: 0.6, //  Creates a Donut Chart. Does not do anything when is3D is enabled
    sliceVisibilityThreshold: 0.02, // Hides slices smaller than 2%
    legend: "none",
    colors: ["#FFC416", "#70B6C1", "#FFB1B7", "#BCD18A", "#D1C28A"],
  }

  // Transform the data with the day as "Mon"
  const transformedWeeklyData = Array.isArray(weekly_payments) && weekly_payments?.length > 0
    ? [
      ["Day", "Revenue"],
      ...weekly_payments.map(payment => [
        moment(payment?.date).format("ddd"), // Format the date to display the day (Mon, Tue, etc.)
        payment?.total
      ])
    ]
    : [["Day", "Revenue"]];

  const areaChartOptions = {
    title: intl.formatMessage({ id: "DashboardData.weeklyRevenue" }),
    isStacked: true,
    height: 300,
    // legend: { position: "top", maxLines: 3 },
    vAxis: { minValue: 0 },
    areaOpacity: 0.1,
    colors: ["#ECBC30", "#70B6C1"],
  };

  const listingTypeHeading = {
    [DEAL_LISTING_TYPE]: intl.formatMessage({ id: "DashboardPage.deals" }),
    [SERVICE_LISTING_TYPE]: intl.formatMessage({ id: "DashboardPage.services" }),
    [PRODUCT_LISTING_TYPE]: intl.formatMessage({ id: "DashboardPage.products" }),
    [STAFF_LISTING_TYPE]: intl.formatMessage({ id: "DashboardPage.staffs" }),
  }
  const arabicLanguage = typeof window !== 'undefined' && localStorage.getItem("language") === "Arabic";

  // if (fetchAnalyticsRequest) {
  //   return <Loader />
  // }

  const handleFlyerDownload = () => {
    const link = document.createElement('a');
    link.href = flyerPdf;
    link.download = 'NOW_BEGINNERS_GUIDE.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const initialStartDate = moment().startOf('day').unix() * 1000;
  const initialEndDate = moment().endOf('day').unix() * 1000;

  const [selectedOption, setSelectedOption] = useState('today');
  const [dateRange, setDateRange] = useState({ rangeStart: initialStartDate, rangeEnd: initialEndDate });
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  const handleSelectChange = (event) => {
    const option = event.target.value;
    setSelectedOption(option);

    let rangeStart, rangeEnd;

    switch (option) {
      case "today":
        rangeStart = moment().startOf('day').unix();
        rangeEnd = moment().endOf('day').unix();
        break;
      case "week":
        rangeStart = moment().startOf('week').unix();
        rangeEnd = moment().endOf('week').unix();
        break;
      case "month":
        rangeStart = moment().startOf('month').unix();
        rangeEnd = moment().endOf('month').unix();
        break;
      case "custom":
        return; // Don't set range yet, waiting for custom date input
      default:
        rangeStart = null;
        rangeEnd = null;
        break;
    }

    // Update the state with the selected date range
    setDateRange({ rangeStart: rangeStart * 1000, rangeEnd: rangeEnd * 1000 });
  };

  const handleCustomDateChange = () => {
    if (!customStartDate || !customEndDate) {
      alert(intl.formatMessage({ id: "DashboardData.customDateAlert" }));
      return;
    }

    const start = moment(customStartDate).unix() * 1000;
    const end = moment(customEndDate).unix() * 1000;
    setDateRange({ rangeStart: start, rangeEnd: end });
  };
  const handleDownloadReport = async () => {
    const userTimezone = moment.tz.guess();

    try {
      const response = await onHandleDownloadReport({
        providerId: currentUserId,
        processName: allProcessNames?.[listingType],
        dateRange: dateRange,
        userTimezone,
      })

      // Create a download link for the response data
      const link = document.createElement('a');
      link.href = URL.createObjectURL(response); // Create object URL for the blob
      link.download = 'report.xlsx'; // Default filename
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading the report:', error);
    }
  };


  useEffect(() => {
    if (dateRange && dateRange.rangeStart && dateRange.rangeEnd) {
      onReFetchAnalytics({ providerId: currentUserId, processName: allProcessNames?.[listingType], dateRange });
    }
  }, [dateRange]);

  if (fetchAnalyticsRequest) {
    return <Loader />
  }

  return (
    <div className={classNames(css.dashboardWrapper, arabicLanguage && css.arabicText)}>
      <div className={css.dashboardBody}>

        {fetchAnalyticsError ? (
          <div className={css.error}>
            <FormattedMessage id="DashboardData.errorMessage" />
          </div>
        ) : null}

        {downloadReportError || downloadReportError ? (
          <div className={css.error}>
            <FormattedMessage id="DashboardData.downloadReportErrorMessage" />
          </div>
        ) : null}

        <div className={css.flyerBanner} onClick={handleFlyerDownload}>
          <div>
            <h4><FormattedMessage id="DashboardData.getStartedWithNow" /></h4>
            <p><FormattedMessage id="DashboardData.clickToDownload" /></p>
          </div>

          <img src={QRfigure} alt='Figure' />
        </div>

        <div className={css.headingNfilter}>
          <div className={css.headingNfilterContainer}>
            {dateRange && dateRange.rangeStart && dateRange.rangeEnd && (
              <div className={css.date_range_display}>
                <p><b>
                  {selectedOption === "today" ? "Today" :
                    `${moment.unix(dateRange.rangeStart / 1000).format('DD MMM')} - ${moment.unix(dateRange.rangeEnd / 1000).format('DD MMM')}`}
                </b>
                </p>
              </div>
            )}

            <div className={css.filter}>
              {/* <label htmlFor="time-filter" className="label">
            <FormattedMessage id="DashboardData.selectTimePeriod" />
           </label> */}

              <div className={css.customSelect}>
                <select
                  id="time-filter"
                  value={selectedOption}
                  onChange={handleSelectChange}
                >
                  <option value="" disabled={true}>{intl.formatMessage({ id: "DashboardData.selectOption" })}</option>
                  <option value="custom">{intl.formatMessage({ id: "DashboardData.customDateOption" })}</option>
                  <option value="today">{intl.formatMessage({ id: "DashboardData.todayOption" })}</option>
                  <option value="week">{intl.formatMessage({ id: "DashboardData.weekOption" })}</option>
                  <option value="month">{intl.formatMessage({ id: "DashboardData.monthOption" })}</option>
                </select>
              </div>

              <div>
                <Button
                  type="button"
                  className={css.downloadReportBtn}
                  onClick={handleDownloadReport}
                  inProgress={downloadReportRequest}
                  disabled={downloadReportRequest}
                  ready={downloadReportSuccess}
                >
                  <FormattedMessage id="DashboardData.downloadButton" />
                </Button>
              </div>

            </div>
          </div>

          {/* mobile */}
          {selectedOption === 'custom' && (
            <div className={css.custom_date_range_wrapper}>
              <div className={css.custom_date_range}>
                <div>
                  <label htmlFor="custom-start-date">
                    {intl.formatMessage({ id: "DashboardData.startDateInputLabel" })}
                  </label>
                  <input
                    id="custom-start-date"
                    type="date"
                    className={css.selectDateInput}
                    placeholder='Select Start Date'
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                  />
                </div>

                <div>
                  <label htmlFor="custom-end-date">
                    {intl.formatMessage({ id: "DashboardData.endDateInputLabel" })}
                  </label>
                  <input
                    id="custom-end-date"
                    type="date"
                    className={css.selectDateInput}
                    placeholder='Select End Date'
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    min={customStartDate}
                  />
                </div>
              </div>
              <Button
                className={css.ApplyDateBtn}
                type="button"
                onClick={handleCustomDateChange}>
                {intl.formatMessage({ id: "DashboardData.ApplyCustomRangeButton" })}
              </Button>
            </div>
          )}


        </div>
        <div className={css.dealsCardsWrapper}>
          <div className={css.dealCard}>
            <div className={css.cardLeft}>
              <div className={css.number}>{activeDeals.length}</div>
              <div className={css.heading}>
                <FormattedMessage id="DashboardPage.activeItems"
                  values={{ listingType: listingTypeHeading?.[listingType] ? listingTypeHeading?.[listingType] : intl.formatMessage({ id: "DashboardPage.items" }) }} />
              </div>
            </div>
            <div className={css.cardRight}>
              <BrandingIconCard type={isDeal ? BrandingIconTypes.ACTIVE_DEALS : BrandingIconTypes.ACTIVE_ITEMS} />
            </div>
          </div>
          <div className={css.dealCard}>
            <div className={css.cardLeft}>
              <div className={css.number}>{isHomePage ? today_sales : filtered_today_sales}</div>
              <div className={css.heading}>
                <FormattedMessage id="DashboardPage.itemsSoldToday"
                  values={{ listingType: listingTypeHeading?.[listingType] ? listingTypeHeading?.[listingType] : intl.formatMessage({ id: "DashboardPage.items" }) }} />
              </div>
            </div>
            <div className={css.cardRight}>
              <BrandingIconCard type={isDeal ? BrandingIconTypes.HEALTH : BrandingIconTypes.SOLD} />
            </div>
          </div>
          <div className={css.dealCard}>
            <div className={css.cardLeft}>
              <div className={css.number}>{totalDeals}</div>
              <div className={css.heading}>
                <FormattedMessage id="DashboardPage.totalItems"
                  values={{ listingType: listingTypeHeading?.[listingType] ? listingTypeHeading?.[listingType] : intl.formatMessage({ id: "DashboardPage.items" }) }} />
              </div>
            </div>
            <div className={css.cardRight}>
              <BrandingIconCard type={BrandingIconTypes.TRENDUP} />
            </div>
          </div>
          <div className={css.dealCard}>
            <div className={css.cardLeft}>
              <div className={css.number}>{isHomePage ? sales : filtered_sales}</div>
              <div className={css.heading}>
                <FormattedMessage id="DashboardPage.totalItemSales"
                  values={{ listingType: listingTypeHeading?.[listingType] ? listingTypeHeading?.[listingType] : intl.formatMessage({ id: "DashboardPage.items" }) }} />
              </div>
            </div>
            <div className={css.cardRight}>
              <BrandingIconCard type={BrandingIconTypes.TOTAL} />
            </div>
          </div>
        </div>

        <div className={css.badgeCardBox}>
          <NamedLink className={css.link} name="BookingGuidePage">
            <div className={classNames(css.badgeCard)} onClick={() => {
              // trackEventsMixpanel(mixPanelEventsConstants.BOOKINGS, {
              //   screen: mixPanelEventsScreens.HOME,
              //   user_id: currentUserId,
              //   user_type: 'Provider',
              //   action_type: mixPanelEventsData.SHARE_BOOKING_LINK
              // })
            }}>
              <div>
                <BrandingIconCard type={badgeIcon.SHARE_LINK} />
              </div>
              <div className={css.heading}>{intl.formatMessage({ id: "DashboardPage.shareBookingLink" })}</div>
            </div>
          </NamedLink>
          <NamedLink className={css.link} name="StripePayoutPage">
            <div onClick={() => { }} className={classNames(css.badgeCard)}>
              <div>
                <BrandingIconCard type={badgeIcon.WALLET} />
              </div>
              <div className={css.heading}>{intl.formatMessage({ id: "DashboardPage.payoutDetails" })}</div>
            </div>
          </NamedLink>
          <NamedLink className={css.link} name="MerchantFlyerPage">
            <div className={classNames(css.badgeCard)}>
              <div>
                <BrandingIconCard type={badgeIcon.SCANQR} />
              </div>
              <div className={css.heading}>{intl.formatMessage({ id: "DashboardPage.getQR" })}</div>
            </div>
          </NamedLink>
          <NamedLink className={css.link} name="ViewListingsPage" params={{ listingType: "staff" }}>
            <div className={classNames(css.badgeCard)}>
              <div>
                <BrandingIconCard type={badgeIcon.PEOPLE} />
              </div>
              <div className={css.heading}>{intl.formatMessage({ id: "DashboardPage.manageStaff" })}</div>
            </div>
          </NamedLink>
        </div>

        {/* Analytics and charts */}
        <div className={css.cartList}>
          <div className={css.ratioCard}>
            <div className={css.heading}>
              <FormattedMessage id="DashboardData.fulfillmentRatio" />
              <span className={css.threeDot}>
                <BrandingIconCard type="threedot" />
              </span>
            </div>
            <div className={css.chartBox}>

              {pieChartHasData ? (
                <Chart
                  chartType="PieChart"
                  data={pieChartData}
                  options={pieChartOptions}
                  width={'100%'}
                  height={'100%'}
                />) : <p><FormattedMessage id="DashboardData.noData" /></p>}
            </div>
          </div>
          <div className={css.ratioCard}>
            <div className={css.heading}>
              <FormattedMessage id="DashboardData.salesVolume" />
              <span className={css.threeDot}>
                <BrandingIconCard type="threedot" />
              </span>
            </div>
            <div className={css.chartBox}>
              {Array.isArray(top_4_services) && top_4_services.length > 0 ?
                top_4_services.map((service, index) => {
                  const dynamicClass = `service${index + 1}`; // Dynamically create class name like service1, service2, etc.
                  const revenueNum = (() => {
                    const n = Number(service?.revenue);
                    return Number.isFinite(n) ? n : 0;
                  })();
                  return (
                    <div key={index} className={classNames(css.service, css[dynamicClass])}>
                      <div className={css.dot}></div>
                      <div>
                        <div className={css.title}>{service?.title}</div>
                        <div className={css.revenue}><CurrencyAmount amount={revenueNum} decimals={3} symbolSize={14} /></div>
                      </div>
                    </div>
                  );
                })
                : null
              }

              {(isHomePage ? revenue : filtered_revenue) ? (
                <div className={css.totalRevenue}>
                  <h6>
                    <FormattedMessage id="DashboardData.totalRevenue" />
                  </h6>
                  <span className={css.totalAmount}>
                    <CurrencyAmount amount={(isHomePage ? revenue : filtered_revenue) ?? 0} decimals={3} symbolSize={18} />
                  </span>
                </div>
              ) : null}

              <Chart
                chartType="PieChart"
                width="100%"
                height="400px"
                data={transformedServiceData}
                options={top4ServicesOptions}
              />
            </div>
          </div>
          <div className={css.ratioCard}>
            <div className={css.heading}>
              <FormattedMessage id="DashboardData.totalRevenue" />

              <span className={css.threeDot}>
                <BrandingIconCard type="threedot" />
              </span>
            </div>
            <p className={css.amount}><CurrencyAmount amount={parseFloat(isHomePage ? revenue : filtered_revenue) || 0} decimals={3} symbolSize={18} /></p>
            <p className={css.allTimeRevenue}>
              <FormattedMessage id="DashboardData.allTimeRevenue" />
            </p>
            <div className={css.chartBox}>
              {weekly_payments?.length > 0 ? (
                <Chart
                  chartType="AreaChart"
                  width="100%"
                  height="100%"
                  data={transformedWeeklyData}
                  options={areaChartOptions}
                />
              ) : <p><FormattedMessage id="DashboardData.noWeeklyData" /></p>}
            </div>
          </div>
        </div>

        <div className={css.bookingOrder}>
          <div className={css.bookingHeader}>
            <div className={css.bookingHeading}>
              {/* {intl.formatMessage({ id: "DashboardPage.latestBookings" })} */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardData;
